import React, { useCallback, useEffect, useState } from 'react';
import { FooterCard, TitleContainer, WrapperButton, WrapperContainer, WrapperSectionAmount } from './CalculateAmount.styles';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { DefaultModal } from '../Modals/DefaultModal/DefaultModal';
import { Button } from '../Button/DefaultButton/Button';
import { AmountInput } from './AmountInput';
import { executeAction } from '@/utils/handleEndpoints';
import _ from 'lodash';
import { convertToNumber } from '@/utils/formatValues';
import { pages } from '@/defaultVerbiages';
import { Spinner } from '../RefreshableCurrencyInput/RefreshableCurrencyInput.styles';
import { setDisableRepriceButton, setIsAtLeastOneRepriceDone } from '@/reducers/VmoV2';

export interface IModalInfoType {
  header?: string;
  bodyLow?: string;
  bodyHigh?: string;
  body?: string;
  button: string;
  auxMinValue?: number;
  auxMaxValue?: number;
  auxValue?: number;
}

interface CalculateAmount {
  selectedProgram: number[];
}

const CalculateAmount: React.FC<CalculateAmount> = ({ selectedProgram }) => {
  let TemplateConfig = useAppSelector((state) => state.TemplateConfig);
  let LoanApplication = useAppSelector((state) => state.LoanApplication);
  const { isGoingBack } = useAppSelector((state) => state.currentPage);
  const { selectedLineAmount, selectedDrawAmount, minLineAmount, maxLineAmount, minCalculatedDrawAmount, maxDrawAmount, disableRepriceButton } = useAppSelector(
    (state) => state.VmoV2,
  );
  const [onRange, setOnRange] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const isICE = TemplateConfig?.isICE ?? false;
  const helKey = isICE ? 'helRates' : 'helData';
  const helocKey = isICE ? 'helocRates' : 'helocData';
  const typeOfProgram = LoanApplication?.selectedProgramType === 'Home Equity Loan' ? helKey : helocKey;
  const isHEL = LoanApplication?.selectedProgramType === 'Home Equity Loan';
  const isDrawAmountHidden = TemplateConfig?.pages?.vmo?.vmoV2?.calculateAmount?.sectionDrawAmount?.isHidden || false;

  useEffect(() => {
    if (selectedProgram.length > 0) dispatch(setDisableRepriceButton(true));
  }, [selectedProgram, dispatch]);

  useEffect(() => {
    if (isGoingBack) {
      return;
    }

    // Only initialize if we don't have values in the Redux store
    if (selectedLineAmount === 0 && selectedDrawAmount === 0) {
      const isLowEquity = LoanApplication.estimatedHomeEquity < 50000;
      const serverMinLine = LoanApplication?.availablePrograms[typeOfProgram]?.rates?.[0]?.minLineAmount || 0;
      const serverMaxLine = LoanApplication?.availablePrograms[typeOfProgram]?.rates?.[0]?.maxLineAmount || 0;
      const serverMinDraw = LoanApplication?.availablePrograms[typeOfProgram]?.rates?.[0]?.minDrawAmount || 0;
      const lineAmount = isLowEquity ? 35000 : serverMinLine;
      const drawAmount = isLowEquity ? 35000 : serverMinDraw;
      dispatch({ type: 'VmoV2/setMinLineAmount', payload: LoanApplication?.availablePrograms[typeOfProgram]?.rates?.[0]?.minLineAmount || 0 });
      dispatch({ type: 'VmoV2/setSelectedLineAmount', payload: lineAmount });
      dispatch({ type: 'VmoV2/setMaxLineAmount', payload: serverMaxLine });
      dispatch({ type: 'VmoV2/setSelectedDrawAmount', payload: drawAmount });
      if (isLowEquity) {
        dispatch({ type: 'VmoV2/setMinCalculatedDrawAmount', payload: lineAmount });
        dispatch({ type: 'VmoV2/setMaxDrawAmount', payload: drawAmount });
      } else {
        dispatch({ type: 'VmoV2/setMinCalculatedDrawAmount', payload: 37500 });
        dispatch({ type: 'VmoV2/setMaxDrawAmount', payload: 50000 });
      }
      // Debug logs removed

      dispatch({
        type: 'LoanApplication/updateLoanApplication',
        payload: {
          ...LoanApplication,
          lineAmount,
          drawAmount,
          minLineAmount: serverMinLine,
          maxLineAmount: serverMaxLine,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minDrawPercentage = typeof TemplateConfig?.minDrawPercentage === 'number' ? TemplateConfig?.minDrawPercentage / 100 : 0.75;
  const isLowLineAmount = (LoanApplication?.availablePrograms?.[helocKey]?.maxLineAmount || 0) <= 49999;

  const calculateAmount = TemplateConfig?.pages?.vmo?.vmoV2?.calculateAmount;
  const { title, sectionLineAmount, sectionDrawAmount } = calculateAmount || {};

  const [modalInfo, setModalInfo] = useState<null | IModalInfoType>(null);

  const handleModal = useCallback((info: IModalInfoType) => {
    setModalInfo(info);
  }, []);

  const getTitleText = () => {
    const DEFAULT_TEXT = 'Line and Draw Amount';
    const baseTitle = title?.title;

    if (isHEL) {
      return baseTitle?.replace(DEFAULT_TEXT, 'Loan Amount') || 'Enter your Loan Amount';
    }

    if (isDrawAmountHidden) {
      return baseTitle?.replace(DEFAULT_TEXT, 'Line Amount') || 'Enter your Line Amount';
    }

    return baseTitle || `Enter your ${DEFAULT_TEXT}`;
  };

  const getLineAmountTitle = () => {
    if (isHEL) {
      const title = sectionLineAmount?.titleLoan || sectionLineAmount?.title || 'Loan Amount';
      return title;
    }
    const title = sectionLineAmount?.title || 'Line Amount';
    return title;
  };

  const handleRepriceRates = async () => {
    setLoading(true);
    dispatch({ type: 'VmoV2/setIsReprice', payload: true });
    // Clear all debt consolidation and credit union states
    dispatch({ type: 'VmoV2/clearSelectedProgramStatus' });
    dispatch({ type: 'VmoV2/clearCheckboxes' });
    let auxLoanApplication = _.cloneDeep(LoanApplication);
    const auxTemplateConfig = { ...TemplateConfig };
    auxLoanApplication.lineAmount = Number(selectedLineAmount);
    const minDrawAmount = auxTemplateConfig?.minDrawPercentage ? (auxTemplateConfig?.minDrawPercentage / 100) * auxLoanApplication.lineAmount : 0;
    auxLoanApplication.drawAmount = selectedDrawAmount || minDrawAmount || 0;
    auxLoanApplication.maxDrawAmount = auxLoanApplication.lineAmount;
    auxLoanApplication.features.vmoRedesign = true;
    auxLoanApplication.programType = undefined;
    auxLoanApplication.programName = undefined;
    auxLoanApplication.selectedProgram = {}; // Clear the selected program
    // Normalize HELOC entries with current amounts for both ICE and non-ICE
    const helocRatesArray = (auxLoanApplication?.availablePrograms as any)?.[helocKey]?.rates || (auxLoanApplication?.availablePrograms as any)?.[helocKey];
    if (Array.isArray(helocRatesArray)) {
      helocRatesArray.forEach((rate: any) => {
        rate.lineAmount = Number(selectedLineAmount);
        rate.drawAmount = Number(selectedDrawAmount);
        rate.maxDrawAmount = Number(selectedLineAmount);
        rate.minDrawAmount = Number(minDrawAmount);
      });
    }
    const helocMaxApproved = (auxLoanApplication?.availablePrograms as any)?.[helocKey]?.maxAmountApproved;
    if (helocMaxApproved && helocMaxApproved > auxLoanApplication?.maxLineAmount) {
      auxLoanApplication.maxLineAmount = helocMaxApproved;
    }
    auxLoanApplication.drawAmount = Number(selectedDrawAmount);
    auxLoanApplication.minDrawAmount = minDrawAmount ?? auxLoanApplication?.lineAmount;
    auxLoanApplication.minLineAmount = minLineAmount;
    auxLoanApplication.maxDrawAmount = auxLoanApplication?.lineAmount;
    auxLoanApplication.equityResponse.estimatedHomeEquity = auxLoanApplication?.estimatedHomeEquity;
    auxLoanApplication.initialLineAmount = auxLoanApplication?.lineAmount;
    if (auxLoanApplication.helocData) {
      auxLoanApplication.helocData.minDrawPercentage = isLowLineAmount ? 100 : 75;
    }
    auxLoanApplication.auxLineAmount = auxLoanApplication?.lineAmount;
    auxLoanApplication.isReprice = true;
    try {
      const response = await executeAction(false, auxLoanApplication, dispatch, TemplateConfig, true);
      setLoading(false);

      if (response && 'data' in response) {
        const responseData = (response.data as any)?.[typeOfProgram]?.[0] || (response.data as any)?.[typeOfProgram]?.rates?.[0];
        const updateProgram = auxLoanApplication?.selectedProgram;
        if (updateProgram?.fees && responseData?.fees && responseData.fees.length > 0) {
          updateProgram.fees[0].feeAmount = responseData?.fees[0]?.feeAmount;
          updateProgram.fees[0].feePercentage = responseData?.fees[0]?.feePercentage;
        }

        const programTypes = [helocKey, helKey] as const;

        programTypes.forEach((programType) => {
          const serverRates = (response.data as any)?.[programType]?.rates || (response.data as any)?.[programType];
          const localPrograms = (auxLoanApplication?.availablePrograms as any)?.[programType];
          if (serverRates && localPrograms) {
            serverRates.map((program: any, index: number) => {
              const hasRatesArray = Array.isArray(localPrograms?.rates);
              const currentLocal = hasRatesArray ? localPrograms?.rates?.[index] : localPrograms?.[index];
              const updatePrograms = {
                ...currentLocal,
                lineAmount: program?.lineAmount,
                maxDrawAmount: program?.maxDrawAmount,
                maxLineAmount: program?.maxLineAmount,
                minDrawAmount: program?.minDrawAmount,
                minLineAmount: program?.minLineAmount,
                apr: program?.apr,
                estimatedMonthlyPayment: program?.estimatedMonthlyPayment,
              };

              if (program?.fees && program.fees.length > 0 && currentLocal?.fees) {
                currentLocal.fees[0].feeAmount = program?.fees[0]?.feeAmount;
                currentLocal.fees[0].feePercentage = program?.fees[0]?.feePercentage;
              }

              if (hasRatesArray) {
                localPrograms.rates[index] = updatePrograms;
              } else {
                localPrograms[index] = updatePrograms;
              }
            });
          }
        });

        const firstRate = (response.data as any)?.[typeOfProgram]?.[0] || (response.data as any)?.[typeOfProgram]?.rates?.[0];
        if (firstRate) {
          auxLoanApplication.minLineAmount = firstRate.minLineAmount;
          auxLoanApplication.maxLineAmount = firstRate.maxLineAmount;
          // Set selectedProgram to reflect the repriced terms so downstream UI can render APR and Monthly Payment
          auxLoanApplication.selectedProgram = {
            ...firstRate,
            programName: firstRate.programName || auxLoanApplication?.selectedProgram?.programName,
          } as any;
        }
      }
      dispatch({
        type: 'LoanApplication/updateLoanApplication',
        payload: auxLoanApplication,
      });
      dispatch(setIsAtLeastOneRepriceDone(true));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      dispatch({ type: 'VmoV2/setIsReprice', payload: false });
      dispatch(setDisableRepriceButton(true));
    }
  };

  const isOnRange = (value: number, propertyType: 'Line Amount' | 'Draw Amount' | 'Loan Amount') => {
    const isLineAmountType = propertyType === 'Line Amount' || propertyType === 'Loan Amount';
    const auxMinValue = isLineAmountType ? minLineAmount : minCalculatedDrawAmount || LoanApplication?.minDrawAmount;
    const auxMaxValue = isLineAmountType ? maxLineAmount : maxDrawAmount || LoanApplication?.maxDrawAmount;
    const isValidValue = isNaN(value) || value == null;
    const onRange = isValidValue || value < auxMinValue || value > auxMaxValue;
    setOnRange(onRange);
  };

  const handleChange = (value: string | number, propertyType?: any) => {
    dispatch({ type: 'VmoV2/setIsReprice', payload: true });
    // clear all debt consolidation and credit union states
    dispatch({ type: 'VmoV2/clearSelectedProgramStatus' });
    dispatch({ type: 'VmoV2/clearCheckboxes' });
    dispatch(setDisableRepriceButton(false));
    const newValue = convertToNumber(value.toString());
    const truncatedLineAmount = Math.trunc(Number(newValue) / 100) * 100;
    const auxMinCalculatedDrawAmount = Math.ceil((Number(minDrawPercentage) * truncatedLineAmount) / 100) * 100;

    if (propertyType === 'Draw Amount') {
      dispatch({ type: 'VmoV2/setSelectedDrawAmount', payload: newValue });
    } else if (isLowLineAmount) {
      dispatch({ type: 'VmoV2/setSelectedLineAmount', payload: newValue });
      dispatch({ type: 'VmoV2/setSelectedDrawAmount', payload: newValue });
      dispatch({ type: 'VmoV2/setMinCalculatedDrawAmount', payload: newValue });
      dispatch({ type: 'VmoV2/setMaxDrawAmount', payload: newValue });
    } else {
      dispatch({ type: 'VmoV2/setSelectedLineAmount', payload: newValue });
      dispatch({ type: 'VmoV2/setSelectedDrawAmount', payload: auxMinCalculatedDrawAmount });
      dispatch({ type: 'VmoV2/setMinCalculatedDrawAmount', payload: auxMinCalculatedDrawAmount });
      dispatch({ type: 'VmoV2/setMaxDrawAmount', payload: truncatedLineAmount });
    }
  };

  const handleValidation = (e: React.ChangeEvent<HTMLInputElement> | string, propertyType: 'Line Amount' | 'Draw Amount' | 'Loan Amount') => {
    dispatch(setDisableRepriceButton(false));
    const amountValue = typeof e === 'string' ? e : e?.target?.value?.replace(/[^0-9]/g, '') || (propertyType === 'Draw Amount' ? selectedDrawAmount : selectedLineAmount);
    const numericAmountValue = Number(amountValue);
    const roundedValue = Math.floor(numericAmountValue / 100) * 100;
    const isLineAmountType = propertyType === 'Line Amount' || propertyType === 'Loan Amount';
    const minAmount = isLineAmountType ? minLineAmount : minCalculatedDrawAmount || LoanApplication?.minDrawAmount;
    const maxAmount = isLineAmountType ? maxLineAmount : maxDrawAmount || LoanApplication?.maxDrawAmount;
    const updateLineAmount = (value: number) => {
      const minCalcDraw = isLowLineAmount ? value : Math.ceil((Number(minDrawPercentage) * value) / 100) * 100;
      dispatch({ type: 'VmoV2/setSelectedLineAmount', payload: value });
      dispatch({ type: 'VmoV2/setSelectedDrawAmount', payload: minCalcDraw });
      dispatch({ type: 'VmoV2/setMinCalculatedDrawAmount', payload: minCalcDraw });
      dispatch({ type: 'VmoV2/setMaxDrawAmount', payload: value });
    };
    if (isLineAmountType) {
      if (roundedValue < minAmount || amountValue === '') {
        updateLineAmount(minAmount);
      } else if (roundedValue > maxAmount) {
        updateLineAmount(maxAmount);
      } else if (roundedValue !== selectedLineAmount) {
        updateLineAmount(roundedValue);
      }
    } else {
      let roundedInputDraw = Math.max(Math.ceil(roundedValue / 100) * 100, minCalculatedDrawAmount || (isLowLineAmount ? selectedLineAmount : LoanApplication?.minDrawAmount));
      roundedInputDraw = Math.min(roundedInputDraw, maxAmount);
      const minCalcDrawAmount = Math.ceil((Number(minDrawPercentage) * Number(selectedLineAmount)) / 100) * 100;
      if (numericAmountValue === 0 || roundedInputDraw === 0) {
        dispatch({ type: 'VmoV2/setSelectedDrawAmount', payload: LoanApplication?.minDrawAmount });
      } else if (numericAmountValue >= minCalcDrawAmount) {
        dispatch({ type: 'VmoV2/setSelectedDrawAmount', payload: roundedInputDraw });
      } else {
        dispatch({ type: 'VmoV2/setSelectedDrawAmount', payload: minCalcDrawAmount });
      }
    }
    isOnRange(minAmount, isLineAmountType ? 'Line Amount' : 'Draw Amount');
  };

  const bodyHigh = TemplateConfig?.pages?.vmo?.vmoInfo?.lineAmount?.lineAmountHigh;
  const bodyLow = TemplateConfig?.pages?.vmo?.vmoInfo?.lineAmount?.lineAmountLow;
  const bodyHighDraw = TemplateConfig?.pages?.vmo?.vmoInfo?.drawAmount?.initialDraw.highLineAmount;
  const bodyLowDraw = TemplateConfig?.pages?.vmo?.vmoInfo?.drawAmount?.initialDraw.lowLineAmount;

  return (
    <WrapperContainer>
      <TitleContainer $isHidden={title?.isHidden ?? false}>
        <p>{getTitleText()}</p>
      </TitleContainer>
      <WrapperSectionAmount>
        <AmountInput
          dataTestId="line-amount"
          title={getLineAmountTitle()}
          minAmount={minLineAmount}
          maxAmount={maxLineAmount}
          handleModal={handleModal}
          handleChange={handleChange}
          info={TemplateConfig?.pages?.vmo?.vmoV2?.calculateAmount?.sectionLineAmount?.infoIcon || pages?.vmo?.vmoV2?.calculateAmount?.sectionLineAmount?.infoIcon}
          bodyHigh={bodyHigh}
          bodyLow={bodyLow}
          handleValidation={handleValidation}
          isHidden={sectionLineAmount?.isHidden ?? false}
        />
        <AmountInput
          dataTestId="draw-amount"
          title={sectionDrawAmount?.title || 'Draw Amount'}
          minAmount={minCalculatedDrawAmount}
          maxAmount={maxDrawAmount}
          handleModal={handleModal}
          handleChange={handleChange}
          info={TemplateConfig?.pages?.vmo?.vmoV2?.calculateAmount?.sectionDrawAmount?.infoIcon || pages?.vmo?.vmoV2?.calculateAmount?.sectionDrawAmount?.infoIcon}
          bodyHigh={bodyHighDraw}
          bodyLow={bodyLowDraw}
          handleValidation={handleValidation}
          isHidden={isHEL || isDrawAmountHidden}
        />
      </WrapperSectionAmount>
      <WrapperButton>
        <Button
          label={!loading ? 'Reprice' : <Spinner />}
          variant="primary"
          buttonSize="s"
          onClick={() => handleRepriceRates()}
          isDisabled={loading || onRange || disableRepriceButton}
        ></Button>
      </WrapperButton>
      <FooterCard />
      {modalInfo && <DefaultModal openModal={!!modalInfo} setOpenModal={() => setModalInfo(null)} infoModal={modalInfo} data-testdataTestId="modal" typeModal="default" />}
    </WrapperContainer>
  );
};

export default CalculateAmount;
